/**
 * Fingerprint imprint locations for "same design group" when suppressing
 * duplicate setup/run. Uses artwork signals (ids, URLs, fabric canvas image
 * srcs / text) — not physical print location names.
 */

export function normalizeFlipFromImprintLabel(locationLabel) {
    if (!locationLabel) return "";
    const s = String(locationLabel).toLowerCase().trim();
    if (s.includes("front")) return "front";
    if (s.includes("back")) return "back";
    if (s.includes("left")) return "left";
    if (s.includes("right")) return "right";
    return s;
}

/** Stable clipart / asset identifiers on Fabric-serialized objects (see DesignStudioV3 fabric toObject extensions). */
const ASSET_IDENTITY_KEYS = [
    "image_name",
    "image_path",
    "name",
    "ctype",
    "iextension",
    "imageName"
];

function pushAssetIdentityTokens(o, tokens, seen) {
    if (!o || typeof o !== "object") return;
    ASSET_IDENTITY_KEYS.forEach((k) => {
        const v = o[k];
        if (v == null || String(v).trim() === "") return;
        const t = `${k}:${String(v).trim().toLowerCase()}`;
        if (seen.has(t)) return;
        seen.add(t);
        tokens.push(t);
    });
}

/**
 * Walk Fabric canvas JSON and collect stable artwork tokens (image src, text,
 * clipart name/path from library objects).
 */
export function computeFingerprintFromFabricCanvasJson(canvasBlob) {
    if (!canvasBlob) return "";
    let root;
    try {
        root =
            typeof canvasBlob === "string"
                ? JSON.parse(canvasBlob)
                : canvasBlob;
    } catch (e) {
        return "";
    }
    if (!root || typeof root !== "object") return "";
    const tokens = [];
    const seen = new Set();

    const normalizeSrc = (src) => {
        if (!src || typeof src !== "string") return "";
        try {
            const u = src.split("?")[0].trim().toLowerCase();
            return u;
        } catch (e) {
            return String(src).toLowerCase();
        }
    };

    const walkObjects = (objects) => {
        if (!objects || !Array.isArray(objects)) return;
        objects.forEach((o) => {
            if (!o || typeof o !== "object") return;
            pushAssetIdentityTokens(o, tokens, seen);
            if (o.type === "image" && o.src) {
                const n = normalizeSrc(o.src);
                if (n) {
                    const t = `img:${n}`;
                    if (!seen.has(t)) {
                        seen.add(t);
                        tokens.push(t);
                    }
                }
            }
            if (
                (o.type === "i-text" || o.type === "text") &&
                o.text != null &&
                String(o.text).trim() !== ""
            ) {
                const t = `txt:${String(o.text).trim()}:${String(
                    o.fontFamily || ""
                ).toLowerCase()}`;
                if (!seen.has(t)) {
                    seen.add(t);
                    tokens.push(t);
                }
            }
            if (o.type === "group" && o.objects) walkObjects(o.objects);
            if (Array.isArray(o.objects)) walkObjects(o.objects);
        });
    };

    walkObjects(root.objects || []);
    return tokens.sort().join("|");
}

/**
 * @param {Record<string,string>} canvasStore - parsed new_canvas_elements
 * @param {string} flip - front | back | left | right
 */
export function fingerprintForFlipFromCanvasStore(canvasStore, flip) {
    if (!canvasStore || !flip) return "";
    const blob = canvasStore[flip];
    return computeFingerprintFromFabricCanvasJson(blob);
}

/**
 * Stamp suppress_design_fingerprint on each selected imprint row from local canvas store.
 */
export function stampImprintLocationsFromCanvasStore(
    imprintLocations,
    canvasStore
) {
    if (!imprintLocations || !Array.isArray(imprintLocations) || !canvasStore) {
        return;
    }
    imprintLocations.forEach((loc) => {
        if (!loc || !loc.is_selected || !loc.location) return;
        const flip = normalizeFlipFromImprintLabel(loc.location);
        const fp = fingerprintForFlipFromCanvasStore(canvasStore, flip);
        // eslint-disable-next-line no-param-reassign
        loc.suppress_design_fingerprint = fp || "";
    });
}

/** Collect image src + text + asset name/path from arbitrary nested payload (design_json, etc.) */
export function computeFingerprintFromArbitraryPayload(payload) {
    if (payload == null) return "";
    let obj = payload;
    if (typeof obj === "string") {
        try {
            obj = JSON.parse(obj);
        } catch (e) {
            return "";
        }
    }
    if (!obj || typeof obj !== "object") return "";
    const tokens = [];
    const seen = new Set();

    const normalizeSrc = (src) => {
        if (!src || typeof src !== "string") return "";
        return src.split("?")[0].trim().toLowerCase();
    };

    const walk = (node, depth) => {
        if (depth > 25 || node == null) return;
        if (typeof node === "string") {
            const n = normalizeSrc(node);
            if (n && (n.startsWith("http") || n.startsWith("//") || n.startsWith("data:"))) {
                const t = `img:${n}`;
                if (!seen.has(t)) {
                    seen.add(t);
                    tokens.push(t);
                }
            }
            return;
        }
        if (Array.isArray(node)) {
            node.forEach((x) => walk(x, depth + 1));
            return;
        }
        if (typeof node !== "object") return;

        pushAssetIdentityTokens(node, tokens, seen);

        if (node.src && typeof node.src === "string") {
            const n = normalizeSrc(node.src);
            if (n) {
                const t = `img:${n}`;
                if (!seen.has(t)) {
                    seen.add(t);
                    tokens.push(t);
                }
            }
        }
        if (
            node.text != null &&
            String(node.text).trim() !== "" &&
            (node.type === "i-text" ||
                node.type === "text" ||
                node.fontFamily != null)
        ) {
            const t = `txt:${String(node.text).trim()}:${String(
                node.fontFamily || ""
            ).toLowerCase()}`;
            if (!seen.has(t)) {
                seen.add(t);
                tokens.push(t);
            }
        }
        Object.keys(node).forEach((k) => walk(node[k], depth + 1));
    };

    walk(obj, 0);
    return tokens.sort().join("|");
}
